/**
 * SoftwareVersionField Component
 *
 * Displays software version with fallback behavior:
 * 1. First checks the Printing record's softwareVersion field
 * 2. Falls back to fetching print-photo event and parsing rawContent.fields.version
 *
 * Mimics hardware-digester behavior with React Admin 5.x and Material-UI v5.
 */

import { useState, useEffect } from 'react';
import { InputLabel, LinearProgress } from '@mui/material';
import { useDataProvider, useRecordContext } from 'react-admin';
import { Printing, EventType, Event } from '../../../types';

interface PrintPhotoEventContent {
  fields?: {
    version?: string;
  };
}

export const SoftwareVersionField = () => {
  const record = useRecordContext<Printing>();
  const provider = useDataProvider();
  const [version, setVersion] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchPrintPhotoEvent = async () => {
      if (!record?.id) {
        setLoading(false);
        return;
      }

      // Check record first (same as hardware-digester)
      if (record.softwareVersion) {
        setVersion(record.softwareVersion);
        setLoading(false);
        return;
      }

      try {
        // Fetch print-photo event type
        const { data: eventTypes } = await provider.getList<EventType>('eventTypes', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'name', order: 'ASC' },
          filter: { name: 'print-photo' },
        });

        if (!eventTypes?.[0]?.id) {
          setLoading(false);
          return;
        }

        // Fetch print-photo event for this printing
        const { data: events } = await provider.getList<Event>('events', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'eventTime', order: 'ASC' },
          filter: {
            eventTypeId: eventTypes[0].id,
            printingId: record.id,
          },
        });

        if (events?.[0]?.rawContent) {
          try {
            const content: PrintPhotoEventContent = JSON.parse(events[0].rawContent);
            setVersion(content?.fields?.version || '');
          } catch {
            // Silently fail if rawContent is not valid JSON
          }
        }
      } catch {
        // Error fetching software version
      } finally {
        setLoading(false);
      }
    };

    fetchPrintPhotoEvent();
  }, [provider, record?.id, record?.softwareVersion]);

  return (
    <>
      <InputLabel shrink={true} sx={{ margin: '8px 0 4px 0' }}>
        <span>Software Version</span>
      </InputLabel>
      {loading ? (
        <div style={{ width: 60 }}>
          <LinearProgress />
        </div>
      ) : (
        <span>{version || 'N/A'}</span>
      )}
    </>
  );
};

SoftwareVersionField.defaultProps = {
  label: 'Software Version',
};
